import React, { useState, useEffect } from 'react';
import {
    Box,
    Container,
    Typography,
    Card,
    Stack,
	Divider,
    Avatar,
    Chip,
    Button,
    Grid,
    Tabs,
    Tab,
    Paper,
    IconButton,
    CircularProgress,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Tooltip
} from '@mui/material';
import {
    ArrowBack,
    WhatsApp,
    LocalOfferOutlined,
    SmartToyOutlined,
    StarOutline,
    Add,
    ReceiptLongOutlined,
    ExpandMore,
    ErrorOutline,
    StorefrontOutlined,
    EventNoteOutlined
} from '@mui/icons-material';
import { useHistory, useParams } from 'react-router-dom/cjs/react-router-dom.min';
import { getClientDetailCrm } from '../../../controllers/company/crm/CrmController';


// ==========================================
// SUB-COMPONENTS (Modular UI)
// ==========================================

const MetricCard = ({ label, value, color = 'text.primary', icon }) => (
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, bgcolor: '#f8fafc', border: '1px solid #f1f5f9' }}>
        <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
            {icon}
            <Typography variant="caption" color="text.secondary" fontWeight="600" textTransform="uppercase">
                {label}
            </Typography>
        </Stack>
        <Typography variant="h6" fontWeight="800" color={color}>
            {value}
        </Typography>
    </Paper>
);


const ProfileSidebar = ({ profile, crmData, computedTags }) => {
    const fullName = `${profile.name || ''} ${profile.last_name || ''}`.trim() || 'Cliente Anónimo';
    const initials = profile.name ? profile.name.charAt(0).toUpperCase() : 'C';
    
    const avgTicket = crmData.total_visits > 0 
        ? (crmData.total_spent / crmData.total_visits).toFixed(2) 
        : '0.00';

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Intl.DateTimeFormat('es-VE', { day: '2-digit', month: 'short', year: 'numeric' }).format(new Date(dateString));
    };

    return (
        <Card variant="outlined" sx={{ borderRadius: 4, border: '1px solid #e2e8f0', p: 3, textAlign: 'center', position: 'sticky', top: 24 }}>
            {/* Cover & Avatar */}
            <Box sx={{ height: 100, bgcolor: 'primary.main', borderRadius: 2, mb: '-50px', backgroundImage: 'linear-gradient(to right, #0f172a, #334155)' }} />
            <Avatar 
                src={profile.profile_image} 
                sx={{ width: 100, height: 100, fontSize: 36, fontWeight: 'bold', bgcolor: '#e2e8f0', color: '#0f172a', border: '4px solid white', mx: 'auto', mb: 2, boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
            >
                {!profile.profile_image && initials}
            </Avatar>

            {/* Basic Info */}
            <Typography variant="h5" fontWeight="800" color="text.primary">{fullName}</Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>{profile.phone || 'Sin teléfono registrado'}</Typography>
            <Typography variant="caption" color="text.disabled" display="block" mb={3}>{profile.email}</Typography>

            {/* Tags */}
            <Stack direction="row" flexWrap="wrap" justifyContent="center" gap={1} mb={4}>
                {computedTags.map(tag => (
                    <Chip key={tag} label={tag} size="small" sx={{ bgcolor: '#fef3c7', color: '#b45309', fontWeight: 600, border: '1px solid #fde68a' }} />
                ))}
                {(crmData.notes || []).map((note, idx) => (
                    <Chip key={idx} label={note} size="small" variant="outlined" sx={{ color: 'text.secondary' }} />
                ))}
                <Tooltip title="Añadir etiqueta interna">
                    <Chip icon={<Add fontSize="small" />} label="Etiqueta" size="small" variant="outlined" sx={{ borderStyle: 'dashed', cursor: 'pointer', '&:hover': { bgcolor: '#f1f5f9' } }} />
                </Tooltip>
            </Stack>

            {/* Quick Actions */}
            <Stack spacing={1.5} mb={4}>
                <Button variant="contained" startIcon={<WhatsApp />} sx={{ bgcolor: '#22c55e', '&:hover': { bgcolor: '#16a34a' }, borderRadius: 2, textTransform: 'none', fontWeight: 600 }} disableElevation>
                    Contactar por WhatsApp
                </Button>
                <Button variant="outlined" startIcon={<LocalOfferOutlined />} sx={{ color: 'text.primary', borderColor: '#e2e8f0', borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>
                    Enviar Promoción
                </Button>
            </Stack>

            {/* LTV SUMMARY BOX (Clean & Unified) */}
            <Box sx={{ bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 3, p: 2.5, textAlign: 'left' }}>
                <Typography variant="overline" color="text.secondary" fontWeight="700" display="block" mb={2}>
                    DATOS DE VALOR (LTV)
                </Typography>
                
                <Stack spacing={2} divider={<Divider flexItem sx={{ borderStyle: 'dashed', borderColor: '#cbd5e1' }} />}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" color="text.secondary" fontWeight="600">Total Gastado</Typography>
                        <Typography variant="subtitle2" fontWeight="800" color="success.main">
                            ${parseFloat(crmData.total_spent || 0).toFixed(2)}
                        </Typography>
                    </Stack>
                    
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" color="text.secondary" fontWeight="600">Ticket Promedio</Typography>
                        <Typography variant="subtitle2" fontWeight="800" color="primary.main">
                            ${avgTicket}
                        </Typography>
                    </Stack>

                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" color="text.secondary" fontWeight="600">Visitas Totales</Typography>
                        <Typography variant="subtitle2" fontWeight="800" color="text.primary">
                            {crmData.total_visits || 0}
                        </Typography>
                    </Stack>

                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" color="text.secondary" fontWeight="600">Última Visita</Typography>
                        <Typography variant="caption" fontWeight="700" color="text.secondary">
                            {formatDate(crmData.last_interaction_at)}
                        </Typography>
                    </Stack>
                </Stack>
            </Box>
        </Card>
    );
};



const RecentOrdersList = ({ orders }) => {
    const formatTime = (dateString) => {
        return new Intl.DateTimeFormat('es-VE', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }).format(new Date(dateString));
    };

    if (!orders || orders.length === 0) {
        return (
            <Box sx={{ py: 8, textAlign: 'center' }}>
                <StorefrontOutlined sx={{ fontSize: 48, color: '#cbd5e1', mb: 2 }} />
                <Typography variant="body1" color="text.secondary" fontWeight="500">No hay pedidos registrados.</Typography>
                <Typography variant="body2" color="text.disabled">Los consumos del cliente aparecerán aquí.</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            {orders.map((order) => (
                <Accordion key={order.order_id} variant="outlined" sx={{ mb: 2, borderRadius: '12px !important', '&:before': { display: 'none' }, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                    <AccordionSummary expandIcon={<ExpandMore />} sx={{ bgcolor: '#f8fafc', '&:hover': { bgcolor: '#f1f5f9' } }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%" pr={2}>
                            <Stack direction="row" alignItems="center" spacing={2}>
                                <Avatar sx={{ bgcolor: 'white', color: 'primary.main', border: '1px solid #e2e8f0', width: 42, height: 42 }}>
                                    <ReceiptLongOutlined fontSize="small" />
                                </Avatar>
                                <Box>
                                    <Typography variant="subtitle2" fontWeight="700">Orden #{order.code}</Typography>
                                    <Typography variant="caption" color="text.secondary">{formatTime(order.created_at)}</Typography>
                                </Box>
                            </Stack>
                            <Box textAlign="right">
                                <Typography variant="subtitle2" fontWeight="800" color="success.main">
                                    ${parseFloat(order.total_amount).toFixed(2)}
                                </Typography>
                                <Chip label={order.status === 6 ? 'Completado' : 'Pendiente'} size="small" color={order.status === 6 ? 'success' : 'warning'} sx={{ height: 20, fontSize: '0.7rem', fontWeight: 'bold' }} />
                            </Box>
                        </Stack>
                    </AccordionSummary>
                    <AccordionDetails sx={{ bgcolor: 'white', borderTop: '1px solid #e2e8f0', p: 3 }}>
                        <Typography variant="overline" color="text.secondary" fontWeight="700" mb={2} display="block">Artículos del pedido</Typography>
                        <Stack spacing={1.5}>
                            {(order.items || []).map((item) => (
                                <Stack key={item.detail_id} direction="row" justifyContent="space-between" alignItems="center" sx={{ borderBottom: '1px dashed #e2e8f0', pb: 1 }}>
                                    <Typography variant="body2" color="text.primary" fontWeight="500">
                                        <Typography component="span" fontWeight="800" color="primary.main" mr={1.5}>{item.quantity}x</Typography>
                                        {item.product_name}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" fontWeight="600">${parseFloat(item.row_total).toFixed(2)}</Typography>
                                </Stack>
                            ))}
                        </Stack>
                    </AccordionDetails>
                </Accordion>
            ))}
        </Box>
    );
};

const OmnichannelTimeline = ({ interactions }) => {
    const formatTime = (dateString) => {
        return new Intl.DateTimeFormat('es-VE', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }).format(new Date(dateString));
    };

    const getInteractionConfig = (type) => {
        switch(type) {
            case 'review': return { icon: <StarOutline fontSize="small" />, color: '#d97706', bgcolor: '#fef3c7', border: '#fde68a' };
            case 'promotion': return { icon: <LocalOfferOutlined fontSize="small" />, color: '#db2777', bgcolor: '#fce7f3', border: '#fbcfe8' };
            case 'ai': return { icon: <SmartToyOutlined fontSize="small" />, color: '#2563eb', bgcolor: '#dbeafe', border: '#bfdbfe' };
            default: return { icon: <EventNoteOutlined fontSize="small" />, color: '#475569', bgcolor: '#f1f5f9', border: '#e2e8f0' };
        }
    };

    if (!interactions || interactions.length === 0) {
        return (
            <Box sx={{ py: 8, textAlign: 'center' }}>
                <Typography variant="body1" color="text.secondary">No hay interacciones registradas.</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 4 }}>
            <Stack spacing={4} sx={{ position: 'relative', '&::before': { content: '""', position: 'absolute', top: 20, bottom: 20, left: 19, width: '2px', bgcolor: '#e2e8f0' } }}>
                {interactions.map((item) => {
                    const config = getInteractionConfig(item.type);
                    return (
                        <Stack key={item.id} direction="row" spacing={3} sx={{ position: 'relative', zIndex: 1 }}>
                            <Box sx={{ mt: 0.5 }}>
                                <Avatar sx={{ width: 40, height: 40, bgcolor: config.bgcolor, color: config.color, border: `2px solid ${config.border}` }}>
                                    {config.icon}
                                </Avatar>
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="caption" fontWeight="700" color="text.secondary" display="block" mb={0.5}>
                                    {formatTime(item.created_at)}
                                </Typography>
                                <Paper elevation={0} sx={{ p: 2.5, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 3, transition: '0.2s', '&:hover': { borderColor: '#cbd5e1', bgcolor: 'white' } }}>
                                    <Typography variant="subtitle2" fontWeight="800" color="text.primary" mb={0.5}>{item.title}</Typography>
                                    <Typography variant="body2" color="text.secondary" lineHeight={1.6}>{item.content}</Typography>
                                </Paper>
                            </Box>
                        </Stack>
                    );
                })}
            </Stack>
        </Box>
    );
};

// ==========================================
// MAIN COMPONENT
// ==========================================

export default function ClientDetailView() {
    const history = useHistory();
    const { customerId } = useParams();

    // --- STATE ---
    const [currentTab, setCurrentTab] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [clientData, setClientData] = useState(null);
    const [hasError, setHasError] = useState(false);

    // --- API FETCH ---
    useEffect(() => {
        let isMounted = true;

        const fetchClientDetails = async () => {
            setIsLoading(true);
            const response = await getClientDetailCrm(customerId);

            if (isMounted) {
                if (response) {
                    setClientData(response);
                } else {
                    setHasError(true);
                }
                setIsLoading(false);
            }
        };

        if (customerId) fetchClientDetails();
        return () => { isMounted = false; };
    }, [customerId]);

    // --- RENDER VALIDATIONS ---
    if (isLoading) {
        return (
            <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f8fafc' }}>
                <CircularProgress size={48} thickness={4} />
            </Box>
        );
    }

    if (hasError || !clientData) {
        return (
            <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', bgcolor: '#f8fafc' }}>
                <ErrorOutline sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                <Typography variant="h6" color="text.primary" fontWeight="700">No se pudo cargar el perfil</Typography>
                <Typography variant="body2" color="text.secondary" mb={3}>El cliente no existe o no hay conexión con el servidor.</Typography>
                <Button variant="outlined" onClick={() => history.push('/crm/client-list')} sx={{ borderRadius: 2, textTransform: 'none' }}>
                    Volver al Directorio
                </Button>
            </Box>
        );
    }

    // --- DATA EXTRACTION (BUG FIX APPLIED) ---
    // Safely unwrap the payload whether it comes nested in 'data' or not.
    const payload = clientData.data ? clientData.data : clientData;
    
    const profile = payload.profile || {};
    const crmData = payload.crm_data || {};
    const recentOrders = payload.recent_orders || [];
    const interactions = payload.interactions || [];

    // --- TAG COMPUTATION ---
    const computedSystemTags = [];
    if (crmData.total_spent >= 500) computedSystemTags.push('VIP');
    if (crmData.total_visits >= 2) computedSystemTags.push('Recurrente');
    if (computedSystemTags.length === 0) computedSystemTags.push('Nuevo');

    return (
        <Box sx={{ minHeight: '100vh', py: 4 }}>
            <Container maxWidth="xl">
                
                {/* Header Navigation */}
                <Stack direction="row" alignItems="center" spacing={2} mb={4}>
                    <IconButton 
                        onClick={() => history.push('/crm/client-list')} 
                        sx={{ bgcolor: 'white', border: '1px solid #e2e8f0', '&:hover': { bgcolor: '#f1f5f9' } }}
                    >
                        <ArrowBack fontSize="small" />
                    </IconButton>
                    <Box>
                        <Typography variant="h5" fontWeight="800" color="text.primary" letterSpacing="-0.5px">Perfil del Cliente</Typography>
                        <Typography variant="body2" color="text.secondary">Gestiona la relación, pedidos e interacciones.</Typography>
                    </Box>
                </Stack>

                <Grid container spacing={4}>
                    
                    {/* Left Column: Profile & Dashboard */}
                    <Grid item xs={12} md={4} lg={3}>
                        <ProfileSidebar profile={profile} crmData={crmData} computedTags={computedSystemTags} />
                    </Grid>

                    {/* Right Column: Interactive Tabs */}
                    <Grid item xs={12} md={8} lg={9}>
                        <Card variant="outlined" sx={{ borderRadius: 4, border: '1px solid #e2e8f0', minHeight: '100%', bgcolor: 'white' }}>
                            
                            <Box sx={{ borderBottom: 1, borderColor: '#e2e8f0', px: 2, pt: 1 }}>
                                <Tabs 
                                    value={currentTab} 
                                    onChange={(e, val) => setCurrentTab(val)} 
                                    sx={{ 
                                        '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: '0.95rem', minHeight: 60 },
                                        '& .Mui-selected': { color: 'primary.main' }
                                    }}
                                >
                                    <Tab label={`Pedidos Recientes (${recentOrders.length})`} />
                                    <Tab label="Historial Omnicanal" />
                                    <Tab label="Notas Internas" />
                                </Tabs>
                            </Box>

                            <Box sx={{ bgcolor: '#ffffff' }}>
                                {currentTab === 0 && <RecentOrdersList orders={recentOrders} />}
                                {currentTab === 1 && <OmnichannelTimeline interactions={interactions} />}
                                {currentTab === 2 && (
                                    <Box sx={{ p: 6, textAlign: 'center' }}>
                                        <Typography variant="body1" color="text.secondary">Espacio para el editor de notas privadas del comercio.</Typography>
                                    </Box>
                                )}
                            </Box>
                        </Card>
                    </Grid>
                </Grid>
            </Container>
        </Box>
    );
}